import { Layers, AlertCircle, RefreshCw } from 'lucide-react';
import { useGetDashboardSdlc } from '@edison/api-client-react';
import { Button } from '@/components/ui/button.js';
import { formatFy } from '@/lib/utils.js';

/**
 * SDLC-stage breakdown of recorded R&D work time, derived server-side from stored
 * work-bucket SDLC tags (GET /api/dashboard/sdlc). Renders the seven canonical SDLC
 * stages as simple horizontal bars sized by weighted hours.
 *
 * IMPORTANT: these are *indicative recorded work-time* figures on a different basis to
 * the capped/classified claim numbers — the copy must never say "claim". Rule Zero: only
 * real hook data is rendered; empty/zero shows a graceful state, never fabricated numbers.
 */
export function SdlcBreakdown({
  incomeYear,
  projectId,
}: {
  incomeYear: string;
  projectId?: string;
}) {
  const { data, isLoading, error, refetch } = useGetDashboardSdlc({
    incomeYear,
    ...(projectId ? { projectId } : {}),
  });

  const scopeLabel = projectId ? 'this project' : formatFy(incomeYear);

  // ── Loading ──────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="rounded-lg border p-5 space-y-3">
        <div className="h-5 w-48 rounded skeleton" />
        {[0, 1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="h-6 rounded skeleton" />
        ))}
      </div>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/5 p-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-destructive text-sm">
          <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>Failed to load SDLC breakdown.</span>
        </div>
        <Button variant="secondary" size="sm" onClick={() => void refetch()}>
          <RefreshCw className="h-3.5 w-3.5" />
          Retry
        </Button>
      </div>
    );
  }

  const result = data?.data;
  const categories = result?.categories ?? [];
  const totalHours = result?.totalHours ?? 0;
  const unmappedHours = result?.unmappedHours ?? 0;

  // ── Empty / zero ─────────────────────────────────────────────────────
  // Rule Zero: no fabricated data — show a plain prompt and 0/— rather than inventing figures.
  if (!result || totalHours === 0 || categories.length === 0) {
    return (
      <div className="rounded-lg border p-5 space-y-3">
        <Header scopeLabel={scopeLabel} />
        <div className="rounded-lg border border-dashed p-8 text-center">
          <Layers className="h-8 w-8 mx-auto text-muted-foreground/40" aria-hidden="true" />
          <p className="mt-3 text-sm text-muted-foreground">No recorded work time yet.</p>
          <p className="text-xs text-muted-foreground mt-1">
            SDLC stages appear here once classified work buckets carry an SDLC tag.
          </p>
        </div>
      </div>
    );
  }

  const maxHours = categories.reduce((max, c) => Math.max(max, c.hours), 0);

  return (
    <div className="rounded-lg border p-5 space-y-3">
      <Header scopeLabel={scopeLabel} />
      <ul className="space-y-2.5">
        {categories.map((c) => {
          const pct = maxHours > 0 ? Math.round((c.hours / maxHours) * 100) : 0;
          return (
            <li key={c.key}>
              <div className="flex items-baseline justify-between gap-3 text-sm">
                <span className="font-medium">{c.label}</span>
                <span className="font-mono tabular-nums text-muted-foreground shrink-0">
                  {c.hours > 0 ? fmtHours(c.hours) : '—'}
                </span>
              </div>
              {/* Bar is purely decorative — the stage label and hours above are the
                  accessible text, so hide it from assistive tech to avoid double-announce. */}
              <div className="mt-1 h-2 w-full rounded-full bg-muted overflow-hidden" aria-hidden="true">
                <div
                  className="h-full rounded-full bg-primary"
                  style={{ width: `${pct}%` }}
                />
              </div>
              {c.exampleLabels.length > 0 && (
                <p className="mt-1 text-xs text-muted-foreground truncate">
                  e.g. {c.exampleLabels.join(', ')}
                </p>
              )}
            </li>
          );
        })}
      </ul>
      {unmappedHours > 0 && (
        <p className="text-xs text-muted-foreground mt-3">
          {fmtHours(unmappedHours)} not attributed to a stage
        </p>
      )}
    </div>
  );
}

function fmtHours(hours: number): string {
  return `${hours.toFixed(1)}h`;
}

function Header({ scopeLabel }: { scopeLabel: string }) {
  return (
    <div>
      <h3 className="font-semibold flex items-center gap-2 text-sm">
        <Layers className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        SDLC stage breakdown
      </h3>
      <p className="text-xs text-muted-foreground mt-0.5">
        {scopeLabel} · recorded work time (indicative)
      </p>
    </div>
  );
}
